/**
 * Back-in-Stock "Notify Me" — Stoic Performance
 *
 * When a customer selects a genuinely sold-out size, inject one reliable,
 * consistently styled "Notify Me When Back in Stock" button that reuses the
 * store's existing Klaviyo back-in-stock system (enabled in layout/theme.liquid).
 *
 * Why we inject it ourselves: Klaviyo's onsite widget already works on fully
 * sold-out products, but it never auto-injects its button on these PARTIAL-stock
 * products — the first size is in stock (so Klaviyo thinks the product is
 * available), and the theme uses custom Alpine variant radios Klaviyo can't read.
 * Klaviyo DOES listen for clicks on any `.klaviyo-bis-trigger` (event delegation)
 * and opens its form for the variant in the URL — so we inject that trigger.
 *
 * HISTORY (2026-08-07): this file also used to render a full-viewport,
 * non-dismissible "sold out" modal that cross-sold an alternative colorway.
 * That was built during the 2026 knee-sleeve stockout and was retired once it
 * had outlived its purpose — it trapped mobile users and locked scroll. The
 * modal is gone; the back-in-stock capture it also carried is kept, because
 * the Klaviyo BIS flow is live and fed by these signups. Full modal source is
 * in git history if it is ever wanted again.
 *
 * Relies on Liquid injecting this global before the script loads:
 *   window.stoicVariants — product.variants (id, title, option1, available)
 *
 * "Truly sold out" is `variant.available === false`. The Essential Preorder app
 * keeps `available === true` for backordered sizes so those stay buyable as a
 * pre-order and correctly get no notify button.
 */

(function () {
  if (!window.stoicVariants) return;

  const variantById = {};
  window.stoicVariants.forEach(function (v) {
    variantById[v.id] = v;
  });

  const NOTIFY_ID = "stoic-bis-inline";

  // ── Inline "Notify Me When Back in Stock" (reuses Klaviyo BIS) ───────────────
  // The Essential Preorder app ALSO injects a Klaviyo trigger on sold-out sizes,
  // but only after a variant interaction — not on direct page load — so it's an
  // unreliable, inconsistent signup path. We inject one reliable, consistently
  // styled button and hide the app's duplicate whenever it appears, so a customer
  // always has exactly one obvious way to sign up.
  function ensureInlineNotify() {
    if (!document.getElementById(NOTIFY_ID)) {
      const wrap = document.querySelector('.product__submit');
      if (wrap) {
        const a = document.createElement("a");
        a.id = NOTIFY_ID;
        a.href = "#";
        a.className = "klaviyo-bis-trigger"; // Klaviyo's delegated click handler opens its form
        a.textContent = "Notify Me When Back in Stock";
        a.style.cssText = [
          "display:block", "width:100%", "box-sizing:border-box", "margin-top:10px",
          "background:#b84e24", "color:#fff",
          "font-weight:800", "font-size:.9rem", "letter-spacing:.04em", "text-transform:uppercase",
          "padding:14px", "border-radius:4px", "text-decoration:none", "text-align:center",
          "cursor:pointer",
        ].join(";");
        wrap.appendChild(a);
      }
    }
    dedupeForeignTriggers();
  }

  // Hide any OTHER Klaviyo back-in-stock trigger on the main product (the app's
  // flaky duplicate). Leaves our button and any triggers in related-product
  // cards / quick-view drawers untouched.
  function dedupeForeignTriggers() {
    // Never suppress the app's button unless ours actually rendered as a
    // replacement (guards against layouts without a .product__submit target).
    if (!document.getElementById(NOTIFY_ID)) return;
    document.querySelectorAll('.klaviyo-bis-trigger').forEach(function (el) {
      if (el.id === NOTIFY_ID) return;
      if (el.closest('product-grid-item, .quickview__modal, .drawer')) return;
      // The app styles its trigger with display:flex !important, so we must
      // override with !important too.
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function removeInlineNotify() {
    const el = document.getElementById(NOTIFY_ID);
    if (el) el.remove();
  }

  // ── Variant check ───────────────────────────────────────────────────────────
  function checkVariant(vid) {
    vid = parseInt(vid);
    const v = variantById[vid];
    // Only act on genuinely sold-out sizes. Backordered sizes keep
    // available === true and stay buyable — no notify button.
    // (Klaviyo's back-in-stock can't target an available/backorder variant, so
    // a notify option there wouldn't work anyway.)
    if (!v || v.available !== false) {
      removeInlineNotify();
      return;
    }
    ensureInlineNotify();
  }

  function checkFromURL() {
    const vid = new URLSearchParams(location.search).get("variant");
    if (vid) checkVariant(vid);
    else removeInlineNotify();
  }

  // ── Watchers ────────────────────────────────────────────────────────────────
  const _push    = history.pushState.bind(history);
  const _replace = history.replaceState.bind(history);
  history.pushState    = function () { _push.apply(history, arguments);    setTimeout(checkFromURL, 30); };
  history.replaceState = function () { _replace.apply(history, arguments); setTimeout(checkFromURL, 30); };
  window.addEventListener("popstate", function () { setTimeout(checkFromURL, 30); });

  document.addEventListener("variant:change", function (e) {
    const vid = (e.detail && e.detail.variant && e.detail.variant.id) || parseInt(e.detail);
    if (vid) checkVariant(vid); else setTimeout(checkFromURL, 50);
  });

  let lastParam = new URLSearchParams(location.search).get("variant");
  setInterval(function () {
    const cur = new URLSearchParams(location.search).get("variant");
    if (cur !== lastParam) {
      lastParam = cur;
      if (cur) checkVariant(cur);
      else removeInlineNotify();
    }
    // The preorder app injects its duplicate trigger a beat AFTER the variant
    // change, so keep suppressing it while a sold-out size stays selected.
    const curV = variantById[parseInt(cur)];
    if (curV && curV.available === false) dedupeForeignTriggers();
  }, 300);

  checkFromURL();
})();
